import { useState } from 'react';

interface NavigationProps {
  scrolled: boolean;
}

const Navigation = ({ scrolled }: NavigationProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          {/* Left Side - Logo and Nav Items */}
          <div className="flex items-center">
            {/* Logo */}
            <button
              onClick={() => scrollToSection('home')}
              className="cursor-pointer flex-shrink-0"
            >
              <img
                src="https://static.readdy.ai/image/102409a7b7d5861e272bf6d8b1888287/bef5ea38eecc1cdff2583b4b950b8fbb.png"
                alt="Dulair"
                className={`h-10 sm:h-12 w-auto transition-all duration-300 ${
                  scrolled ? 'brightness-0' : 'brightness-100'
                }`}
              />
            </button>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-6 ml-8">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                About Us
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                Our Services
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                Portfolio
              </button>
              <button
                onClick={() => scrollToSection('team')}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                Team
              </button>
            </div>
          </div>

          {/* Right Side - Contact Button */}
          <div className="hidden lg:flex items-center">
            <button
              onClick={() => scrollToSection('contact')}
              className={`px-6 py-2.5 bg-gold text-navy text-sm font-semibold rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled ? '' : 'shadow-lg'
              }`}
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              scrolled ? 'text-navy' : 'text-white'
            }`}
            aria-label="Toggle menu"
            type="button"
          >
            <div className="w-6 h-5 relative flex flex-col justify-between">
              <span 
                className={`w-full h-0.5 transition-all duration-300 ${
                  scrolled ? 'bg-navy' : 'bg-white'
                } ${
                  mobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span 
                className={`w-full h-0.5 transition-all duration-300 ${
                  scrolled ? 'bg-navy' : 'bg-white'
                } ${
                  mobileMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span 
                className={`w-full h-0.5 transition-all duration-300 ${
                  scrolled ? 'bg-navy' : 'bg-white'
                } ${
                  mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleMenu}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-[72px] left-0 right-0 bg-white shadow-xl z-40 lg:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="px-4 py-6 space-y-2">
          <button
            onClick={() => scrollToSection('home')}
            className="block w-full text-left px-4 py-3 text-navy hover:text-gold hover:bg-gold/10 rounded-lg transition-colors cursor-pointer text-sm font-medium"
            type="button"
          >
            Home
          </button>
          <button
            onClick={() => scrollToSection('about')}
            className="block w-full text-left px-4 py-3 text-navy hover:text-gold hover:bg-gold/10 rounded-lg transition-colors cursor-pointer text-sm font-medium"
            type="button"
          >
            About Us
          </button>
          <button
            onClick={() => scrollToSection('services')}
            className="block w-full text-left px-4 py-3 text-navy hover:text-gold hover:bg-gold/10 rounded-lg transition-colors cursor-pointer text-sm font-medium"
            type="button"
          >
            Our Services
          </button>
          <button
            onClick={() => scrollToSection('portfolio')}
            className="block w-full text-left px-4 py-3 text-navy hover:text-gold hover:bg-gold/10 rounded-lg transition-colors cursor-pointer text-sm font-medium"
            type="button"
          >
            Portfolio
          </button>
          <button
            onClick={() => scrollToSection('team')}
            className="block w-full text-left px-4 py-3 text-navy hover:text-gold hover:bg-gold/10 rounded-lg transition-colors cursor-pointer text-sm font-medium"
            type="button"
          >
            Team
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="block w-full px-4 py-3 bg-gold text-navy text-sm font-semibold rounded-lg hover:opacity-90 transition-all cursor-pointer text-center mt-2"
            type="button"
          >
            Contact Us
          </button>
        </div>
      </div>
    </>
  );
};

export default Navigation;
